// packages/storefront/src/components/themes/widgetScopedBaseline.ts
import type { Theme } from '@mui/material/styles';

/**
 * MUI's `CssBaseline`, scoped to one widget (apps/embed/AGENTS.md: global styles are scoped to the widget's mount
 * node): the box model, font smoothing, the theme's body text and its colour, and bold, on the widget's mount node
 * (`#{rootId}`) and on the layers MUI portals its dialogs, menus, popovers and tooltips into (they render at the end
 * of the page's body, outside the mount node). A global `CssBaseline`, which the widgets mounted before, restyled the
 * partner page's own body (its margin, font, colour and a white background), which a dark brand's shop, see-through on
 * its own website's dark page, could not live with (docs/brand-colours.md E10, phase 3).
 *
 * The descendants' `box-sizing: inherit` sits under `:where()`, which weighs nothing, as CssBaseline's `*` does: under
 * `:is()` it would weigh as much as the id and beat every MUI component's own box model, and an outlined text field
 * (`content-box` in MUI's class) would shrink to 33px with its label sitting on the bottom border.
 */
export function widgetScopedBaseline(rootId: string): (theme: Theme) => Record<string, object> {
  return baselineOn(`#${rootId}, .MuiModal-root, .MuiPopper-root`);
}

/**
 * The shop's base styles once it is sealed in its shadow roots (docs/embed-shadow-dom.md): on its surfaces (the mount
 * node's, the overlay layer its dialogs, menus and tooltips render into, and the cart bar's beside the page's header),
 * which inherit nothing from the page (`all: initial`, the sealed widget's base sheet) and pass these down instead.
 */
export const sealedScopedBaseline = baselineOn('.tt-surface');

/** The base styles on the elements `selectors` names, and the box model under them. */
function baselineOn(selectors: string): (theme: Theme) => Record<string, object> {
  const layers = `:is(${selectors})`;
  const anyLayer = `:where(${selectors})`;
  return (theme) => ({
    [layers]: {
      boxSizing: 'border-box',
      WebkitFontSmoothing: 'antialiased',
      MozOsxFontSmoothing: 'grayscale',
      ...theme.typography.body1,
      color: theme.palette.text.primary,
    },
    [`${anyLayer} *, ${anyLayer} *::before, ${anyLayer} *::after`]: { boxSizing: 'inherit' },
    [`${layers} :is(strong, b)`]: { fontWeight: theme.typography.fontWeightBold },
  });
}

/** The shop widget's base styles (`StorefrontApp`'s `scopedBaseline`). */
export const storefrontScopedBaseline = widgetScopedBaseline('tourtask-booking-root');
